import React from "react";
import { CheckCircle, Loader2 } from "lucide-react";
import {
  getControl,
  type ControlOrigination,
  type ControlResponse,
  type ImplementationStatus,
  type SspComponentResponse,
} from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";
import { IMPLEMENTATION_STATUSES } from "../../constants";

import type { ControlImplFormShape } from "../../types";
export type { ControlImplFormShape };

export interface AddControlImplementationDialogProps {
  open: boolean;
  onClose: () => void;
  handleAddControlImpl: () => void;
  addingControlImpl: boolean;
  controlImplForm: ControlImplFormShape;
  setControlImplForm: React.Dispatch<React.SetStateAction<ControlImplFormShape>>;
  sspComponents: SspComponentResponse[];
  ciControls: ControlResponse[];
  ciLoadingControls: boolean;
  ciCatalogId: string;
  ciSelectedControlDetail: ControlResponse | null;
  setCiSelectedControlDetail: React.Dispatch<
    React.SetStateAction<ControlResponse | null>
  >;
  ciLoadingControlDetail: boolean;
  setCiLoadingControlDetail: React.Dispatch<React.SetStateAction<boolean>>;
}

export function AddControlImplementationDialog({
  open,
  onClose,
  handleAddControlImpl,
  addingControlImpl,
  controlImplForm,
  setControlImplForm,
  sspComponents,
  ciControls,
  ciLoadingControls,
  ciCatalogId,
  ciSelectedControlDetail,
  setCiSelectedControlDetail,
  ciLoadingControlDetail,
  setCiLoadingControlDetail,
}: AddControlImplementationDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Control Implementation"
      description="Document how a specific control is satisfied by this system"
      size="md"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={handleAddControlImpl}
          submitLabel={addingControlImpl ? "Saving" : "Save"}
          submitDisabled={
            controlImplForm.componentIds.length === 0 ||
            !controlImplForm.source ||
            !controlImplForm.controlId ||
            !controlImplForm.implementationStatus ||
            addingControlImpl
          }
        />
      }
    >
      {/* Component Selection (multi-select) */}
      {sspComponents.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-300 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-300 rounded-md p-3 text-sm">
          No components found. Please create a Component first before adding
          Control Implementations.
        </div>
      ) : (
        <FormField label="Components" required>
          <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-40 overflow-y-auto">
            {sspComponents.map((c) => {
              const checked = controlImplForm.componentIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setControlImplForm((f) => ({
                        ...f,
                        componentIds: checked
                          ? f.componentIds.filter((id) => id !== c.id)
                          : [...f.componentIds, c.id],
                      }))
                    }
                    className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                  />
                  <span className="text-sm text-foreground">
                    {c.title}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({c.componentType})
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </FormField>
      )}

      {/* Control ID + Description */}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Control ID" required>
          <FormSelect
            options={ciControls.map((c) => ({
              label: c.controlId,
              value: c.controlId,
            }))}
            placeholder={
              ciLoadingControls ? "Loading controls" : "Select a control"
            }
            value={controlImplForm.controlId}
            onChange={async (v) => {
              const ctrl = ciControls.find((c) => c.controlId === v);
              setControlImplForm((f) => ({
                ...f,
                controlId: v,
                description: ctrl?.title || "",
                setParameters: [],
                statements: [],
              }));
              setCiSelectedControlDetail(null);
              if (ctrl) {
                setCiLoadingControlDetail(true);
                try {
                  const res = await getControl(ciCatalogId, ctrl.controlId);
                  setCiSelectedControlDetail(res.data);
                  // Auto-populate parameters from the control
                  if (res.data.params && res.data.params.length > 0) {
                    setControlImplForm((f) => ({
                      ...f,
                      setParameters: res.data.params.map((p) => ({
                        paramId: p.paramId,
                        values: "",
                        remarks: "",
                      })),
                    }));
                  }
                  // Auto-populate all statements (mandatory)
                  const allStmts: {
                    statementId: string;
                    description: string;
                    sortOrder: string;
                  }[] = [];
                  res.data.parts
                    .filter((p) => p.name === "statement")
                    .forEach((part) => {
                      allStmts.push({
                        statementId: part.partId,
                        description: part.prose || "",
                        sortOrder: String(allStmts.length),
                      });
                      part.parts.forEach((sub) => {
                        allStmts.push({
                          statementId: sub.partId,
                          description: sub.prose || "",
                          sortOrder: String(allStmts.length),
                        });
                      });
                    });
                  if (allStmts.length > 0) {
                    setControlImplForm((f) => ({
                      ...f,
                      statements: allStmts,
                    }));
                  }
                } catch {
                  setCiSelectedControlDetail(null);
                } finally {
                  setCiLoadingControlDetail(false);
                }
              }
            }}
          />
        </FormField>
        <FormField label="Description">
          <FormInput
            placeholder="Auto-filled from control"
            value={controlImplForm.description}
            onChange={(v) =>
              setControlImplForm((f) => ({ ...f, description: v }))
            }
          />
        </FormField>
      </div>

      {/* ── Control Detail (from catalog) ── */}
      {ciLoadingControlDetail && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="size-4 animate-spin" />
          Loading control details
        </div>
      )}

      {ciSelectedControlDetail && !ciLoadingControlDetail && (
        <>
          {/* ── Guidance (read-only) ── */}
          {ciSelectedControlDetail.parts.filter((p) => p.name === "guidance")
            .length > 0 && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                  Guidance
                </p>
                {ciSelectedControlDetail.parts
                  .filter((p) => p.name === "guidance")
                  .map((part) => (
                    <p
                      key={part.id}
                      className="text-xs text-amber-700 dark:text-amber-300/80"
                    >
                      {part.prose}
                    </p>
                  ))}
              </div>
            )}

          {/* ── Statements (read-only, auto-included) ── */}
          {ciSelectedControlDetail.parts.filter((p) => p.name === "statement")
            .length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-semibold text-foreground">
                  Statements
                </p>
                {ciSelectedControlDetail.parts
                  .filter((p) => p.name === "statement")
                  .map((part) => {
                    const allStatements = [
                      { id: part.partId, prose: part.prose },
                      ...part.parts.map((sub) => ({
                        id: sub.partId,
                        prose: sub.prose,
                      })),
                    ];
                    return allStatements.map((stmt) => (
                      <div
                        key={stmt.id}
                        className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                      >
                        <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] font-bold text-muted-foreground ">
                            {stmt.id}
                          </span>
                          {stmt.prose && (
                            <p className="text-xs text-foreground mt-0.5">
                              {stmt.prose}
                            </p>
                          )}
                        </div>
                      </div>
                    ));
                  })}
              </div>
            )}

          {/* ── Parameters (value only) ── */}
          {ciSelectedControlDetail.params.length > 0 && (
            <div className="rounded-lg border border-border p-3 space-y-3">
              <p className="text-xs font-semibold text-foreground">
                Parameters
              </p>
              {controlImplForm.setParameters.map((param, idx) => {
                const catalogParam = ciSelectedControlDetail.params.find(
                  (p) => p.paramId === param.paramId,
                );
                return (
                  <div
                    key={param.paramId}
                    className="rounded-lg border border-dashed border-border p-3 space-y-1"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground ">
                        {param.paramId}
                      </span>
                      {catalogParam?.label && (
                        <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                          {catalogParam.label}
                        </span>
                      )}
                    </div>
                    <FormInput
                      placeholder={
                        catalogParam?.values && catalogParam.values.length > 0
                          ? `e.g. ${catalogParam.values.join(", ")}`
                          : "Enter value"
                      }
                      value={param.values}
                      onChange={(v) =>
                        setControlImplForm((f) => ({
                          ...f,
                          setParameters: f.setParameters.map((p, i) =>
                            i === idx ? { ...p, values: v } : p,
                          ),
                        }))
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Implementation Status + Responsible Role IDs */}
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Implementation Status" required>
          <FormSelect
            options={IMPLEMENTATION_STATUSES}
            placeholder="Select status"
            value={controlImplForm.implementationStatus}
            onChange={(v) =>
              setControlImplForm((f) => ({
                ...f,
                implementationStatus: v as ImplementationStatus,
              }))
            }
          />
        </FormField>
        <FormField label="Responsible Role IDs" hint="Comma-separated">
          <FormInput
            placeholder="e.g. isso, system-owner"
            value={controlImplForm.responsibleRoleIds}
            onChange={(v) =>
              setControlImplForm((f) => ({ ...f, responsibleRoleIds: v }))
            }
          />
        </FormField>
      </div>
    </Modal>
  );
}
